/**
 * extra-services-c.ts — Wave 170
 *
 * Third batch of adjacent, high-demand services. Same contract as
 * src/data/extra-services.ts (types imported type-only so there is no runtime
 * import cycle).
 *
 * Rules:
 *  - no pricing anywhere (never in meta, never on page)
 *  - no guarantees; every duration is an explicit working-day range
 *  - Apostille wording follows src/data/apostille-status.ts (Thailand's
 *    accession enters into force 28 Feb 2027; until then the consular
 *    legalisation chain applies)
 */
import type { ExtraService } from "./extra-services";

export const EXTRA_SERVICES_C: ExtraService[] = [
  {
    slug: "director-signature-certification",
    cluster: "notary",
    th: {
      title: "รับรองลายมือชื่อกรรมการบริษัทสำหรับใช้ต่างประเทศ",
      h1: "รับรองลายมือชื่อกรรมการ (Director Signature Certification) พร้อมต่อกงสุลและสถานทูต",
      metaTitle: "รับรองลายมือชื่อกรรมการบริษัท โดยทนาย Notary ครบสาย",
      metaDesc:
        "รับรองลายมือชื่อกรรมการผู้มีอำนาจสำหรับเปิดบัญชีต่างประเทศ ตั้งบริษัทลูก และเซ็นสัญญาข้ามประเทศ พร้อมแปลรับรองและรับรองกงสุล–สถานทูตในสายเดียว",
      answer:
        "การรับรองลายมือชื่อกรรมการคือการที่ทนายผู้ทำคำรับรองลายมือชื่อและเอกสารตรวจบัตรประชาชนหรือหนังสือเดินทางของกรรมการ ตรวจหนังสือรับรองบริษัทว่ากรรมการผู้นั้นมีอำนาจลงนามจริง แล้วให้กรรมการลงนามต่อหน้า ก่อนออกคำรับรอง โดยทั่วไปงานเสร็จภายใน 1 วันทำการเมื่อกรรมการมาลงนามพร้อมเอกสารครบ และหากปลายทางต้องการรับรองกงสุลต่อ ให้เผื่อเวลาเพิ่มอีก 3–5 วันทำการ",
      audience: "บริษัทที่เปิดบัญชีธนาคารต่างประเทศ ตั้งบริษัทลูก ยื่นประมูลข้ามประเทศ หรือเซ็นสัญญากับคู่ค้าต่างชาติ",
      facts: [
        { label: "ผู้ลงนามรับรอง", value: "ทนายผู้ทำคำรับรองลายมือชื่อและเอกสาร (Notarial Services Attorney) ขึ้นทะเบียนกับสภาทนายความ" },
        { label: "ระยะเวลาโดยทั่วไป", value: "1 วันทำการ (เฉพาะขั้นทนาย)" },
        { label: "ต่อกงสุล/สถานทูต", value: "เพิ่ม 3–5 วันทำการ ขึ้นกับคิวของแต่ละสถานทูต" },
        { label: "กรรมการต้องมาเอง", value: "ต้องลงนามต่อหน้าทนาย ไม่รับรองย้อนหลังจากลายเซ็นที่เซ็นมาแล้ว" },
        { label: "อายุหนังสือรับรองบริษัท", value: "ปลายทางส่วนใหญ่ขอฉบับคัดไม่เกิน 3–6 เดือน" },
      ],
      documents: [
        "บัตรประชาชนหรือหนังสือเดินทางของกรรมการผู้ลงนาม (ฉบับจริง)",
        "หนังสือรับรองบริษัทฉบับคัดใหม่จากกรมพัฒนาธุรกิจการค้า",
        "หนังสือบริคณห์สนธิหรือข้อบังคับบริษัท หากปลายทางร้องขอ",
        "เอกสารที่ต้องลงนาม (ยังไม่ต้องเซ็นมาก่อน)",
        "รายละเอียดหน่วยงานปลายทางและประเทศที่จะใช้",
      ],
      steps: [
        { title: "ตรวจอำนาจลงนาม", detail: "เทียบชื่อกรรมการกับหนังสือรับรองบริษัทและเงื่อนไขการลงนาม เช่น ต้องลงนามร่วมหรือประทับตรา", who: "ทีมกฎหมาย", eta: "ภายในวันเดียว" },
        { title: "ตรวจร่างเอกสาร", detail: "ตรวจถ้อยคำและช่องลงนามให้ตรงกับที่ปลายทางกำหนด ก่อนนัดเซ็น", who: "ที่ปรึกษาเอกสาร", eta: "ภายในวันเดียว" },
        { title: "ลงนามต่อหน้าทนาย", detail: "กรรมการแสดงเอกสารตัวจริงและลงนามต่อหน้า ทนายบันทึกและออกคำรับรอง", who: "ทนายผู้ทำคำรับรอง", eta: "1 วันทำการ" },
        { title: "แปลรับรอง (ถ้าจำเป็น)", detail: "แปลชุดเอกสารเป็นภาษาปลายทางพร้อมคำรับรองการแปล", who: "ทีมแปล", eta: "1–3 วันทำการ" },
        { title: "รับรองกงสุลและสถานทูต", detail: "ยื่นกรมการกงสุลและสถานทูตปลายทางตามลำดับที่ถูกต้อง", who: "ทีมเดินเอกสาร", eta: "3–5 วันทำการ" },
      ],
      pitfalls: [
        "กรรมการเซ็นมาก่อนแล้วขอให้รับรองย้อนหลัง ทำไม่ได้",
        "หนังสือรับรองบริษัทเก่าเกินกำหนดที่ปลายทางรับ",
        "เงื่อนไขลงนามระบุกรรมการสองคน แต่มาเซ็นคนเดียว",
        "สะกดชื่อกรรมการภาษาอังกฤษไม่ตรงกับหนังสือเดินทาง",
        "ข้ามลำดับ ยื่นสถานทูตก่อนกรมการกงสุล ทำให้ถูกตีกลับ",
      ],
      compare: [
        { option: "รับรองลายมือชื่อเพียงอย่างเดียว", useWhen: "ปลายทางรับเอกสารที่ทนายรับรองโดยตรง", time: "1 วันทำการ", note: "สั้นที่สุด" },
        { option: "รับรองลายมือชื่อ + แปลรับรอง", useWhen: "ปลายทางใช้ภาษาอื่น", time: "2–4 วันทำการ", note: "แปลและต้นฉบับเย็บชุดเดียวกัน" },
        { option: "ครบสายถึงสถานทูต", useWhen: "ธนาคารหรือนายทะเบียนต่างประเทศบังคับ", time: "5–10 วันทำการ", note: "ขึ้นกับคิวสถานทูต" },
      ],
      faqs: [
        { q: "กรรมการอยู่ต่างประเทศ ทำอย่างไร", a: "ให้ลงนามต่อหน้า Notary Public ในประเทศนั้นแล้วรับรองกลับผ่านสถานทูตไทย หรือรอกลับมาลงนามในไทย เราช่วยวางลำดับให้ตามปลายทาง" },
        { q: "ต้องใช้ตราบริษัทไหม", a: "ขึ้นกับข้อบังคับบริษัท ถ้าหนังสือรับรองระบุว่าต้องประทับตรา ต้องนำตรามาด้วย" },
        { q: "รับรองเป็นภาษาอังกฤษได้ไหม", a: "ได้ คำรับรองออกเป็นภาษาอังกฤษเป็นมาตรฐานสำหรับใช้ต่างประเทศ" },
        { q: "ใช้กับการเปิดบัญชีธนาคารต่างประเทศได้ไหม", a: "ได้ แต่ธนาคารแต่ละแห่งมีแบบฟอร์มเฉพาะ ควรส่งแบบฟอร์มมาให้ตรวจก่อนนัดเซ็น" },
        { q: "ต้องรับรองกงสุลทุกครั้งหรือไม่", a: "ไม่จำเป็นเสมอไป ขึ้นกับหน่วยงานปลายทาง หลายกรณีรับคำรับรองทนายพร้อมแปลรับรองก็พอ" },
        { q: "หลังไทยเข้าเป็นภาคีอนุสัญญา Apostille แล้วจะง่ายขึ้นไหม", a: "การเข้าเป็นภาคีมีผลใช้บังคับวันที่ 28 กุมภาพันธ์ 2570 ก่อนหน้านั้นยังใช้สายรับรองกงสุลตามเดิม" },
        { q: "เอกสารหลายฉบับต้องเซ็นทีละใบไหม", a: "เซ็นในคราวเดียวได้ ทนายบันทึกรายการเอกสารทั้งชุดในคำรับรอง" },
        { q: "มีบริการนัดนอกสถานที่ไหม", a: "มี สำหรับคณะกรรมการที่ประชุมพร้อมกัน สามารถนัดที่สำนักงานบริษัทได้" },
      ],
      consultant:
        "ทีมงานประสบการณ์กว่า 15 ปีจะตรวจเงื่อนไขอำนาจลงนามและข้อกำหนดปลายทางให้ก่อนนัดเซ็น เพื่อไม่ให้เสียเที่ยวและไม่ต้องเซ็นซ้ำ หากไม่อยากยุ่งยาก ให้เราดูแลตั้งแต่ตรวจร่างจนถึงรับรองสถานทูตในสายเดียว",
      related: [
        ["บริการรับรองเอกสารโดยทนาย Notary", "/notary"],
        ["รับรองเอกสารกงสุลและสถานทูต", "/mfa-legalisation"],
        ["แปลเอกสารรับรอง", "/translation"],
      ],
    },
    en: {
      title: "Director signature certification for overseas use",
      h1: "Director Signature Certification with consular and embassy legalisation",
      metaTitle: "Director Signature Certification by Thai Notarial Attorney",
      metaDesc:
        "Certify an authorised director's signature for overseas bank accounts, subsidiary registration and cross-border contracts, with certified translation and consular legalisation handled end to end.",
      answer:
        "A director signature certification means a Thai notarial services attorney verifies the director's ID or passport, checks the company affidavit to confirm signing authority, witnesses the signature, and issues a certificate. The notarial step normally completes within one working day when the director attends with complete documents; add three to five working days if consular legalisation is required.",
      audience: "Companies opening overseas bank accounts, registering subsidiaries, bidding abroad or signing contracts with foreign counterparties.",
      facts: [
        { label: "Certifying officer", value: "Notarial Services Attorney registered with the Lawyers Council of Thailand" },
        { label: "Typical duration", value: "1 working day for the notarial step" },
        { label: "Consular and embassy", value: "Add 3–5 working days depending on embassy queues" },
        { label: "Director attendance", value: "Signature must be witnessed; pre-signed documents cannot be certified retroactively" },
        { label: "Affidavit freshness", value: "Most destinations require a company affidavit issued within 3–6 months" },
      ],
      documents: [
        "Director's original ID card or passport",
        "Recently issued company affidavit from the Department of Business Development",
        "Memorandum or articles of association if the destination requests them",
        "The documents to be signed, left unsigned",
        "Details of the receiving authority and destination country",
      ],
      steps: [
        { title: "Verify signing authority", detail: "Match the director against the affidavit and the signing conditions, such as joint signature or company seal.", who: "Legal team", eta: "Same day" },
        { title: "Review the draft", detail: "Check wording and signature blocks against the destination's requirements before booking the signing.", who: "Document consultant", eta: "Same day" },
        { title: "Sign before the attorney", detail: "The director presents originals and signs in person; the attorney records the act and issues the certificate.", who: "Notarial attorney", eta: "1 working day" },
        { title: "Certified translation if required", detail: "Translate the set into the destination language with a translation certificate.", who: "Translation team", eta: "1–3 working days" },
        { title: "Consular and embassy legalisation", detail: "File with the Department of Consular Affairs and then the destination embassy in the correct order.", who: "Filing team", eta: "3–5 working days" },
      ],
      pitfalls: [
        "Documents signed in advance and submitted for retroactive certification",
        "A company affidavit older than the destination accepts",
        "Joint signing required but only one director attends",
        "English spelling of the director's name not matching the passport",
        "Filing with the embassy before the Department of Consular Affairs",
      ],
      compare: [
        { option: "Signature certification only", useWhen: "The destination accepts an attorney certificate directly", time: "1 working day", note: "Shortest route" },
        { option: "Certification plus certified translation", useWhen: "The destination works in another language", time: "2–4 working days", note: "Translation bound with the original" },
        { option: "Full chain to the embassy", useWhen: "Required by a foreign bank or registrar", time: "5–10 working days", note: "Depends on embassy queues" },
      ],
      faqs: [
        { q: "The director is abroad. What now?", a: "Sign before a local notary public and legalise back through the Royal Thai Embassy, or sign in Thailand later. We map the order for your destination." },
        { q: "Is a company seal needed?", a: "It depends on the articles of association. If the affidavit states a seal is required, bring it." },
        { q: "Can the certificate be in English?", a: "Yes, English certificates are standard for overseas use." },
        { q: "Is this accepted for foreign bank account opening?", a: "Usually yes, but banks use their own forms, so send the form for review before the signing appointment." },
        { q: "Is consular legalisation always needed?", a: "No. Many destinations accept the attorney certificate with a certified translation." },
        { q: "Will Apostille simplify this?", a: "Thailand's accession takes effect on 28 February 2027; until then the consular legalisation chain applies." },
        { q: "Must each document be signed separately?", a: "No, a single session can cover the whole set; the certificate lists every document." },
        { q: "Do you offer off-site signing?", a: "Yes, at your office when the board is meeting together." },
      ],
      consultant:
        "Our team of 15+ years reviews signing authority and destination requirements before the appointment so nothing has to be signed twice. If you prefer to hand it over, we run the chain from draft review to embassy legalisation.",
      related: [
        ["Notary Public service", "/en/notary"],
        ["MFA and embassy legalisation", "/en/mfa-legalisation"],
        ["Certified translation", "/en/translation"],
      ],
    },
  },
  {
    slug: "family-court-document-translation",
    cluster: "certified",
    th: {
      title: "แปลและรับรองเอกสารศาลครอบครัวสำหรับใช้ต่างประเทศ",
      h1: "แปลเอกสารศาลครอบครัว คำพิพากษา และคำสั่งศาล พร้อมรับรองครบสาย",
      metaTitle: "แปลเอกสารศาลครอบครัว คำพิพากษา รับรองกงสุลครบสาย",
      metaDesc:
        "แปลคำพิพากษาหย่า คำสั่งอำนาจปกครองบุตร และเอกสารศาลครอบครัว พร้อมคำรับรองการแปล รับรองทนาย และรับรองกงสุล–สถานทูตให้ครบสายในที่เดียว",
      answer:
        "เอกสารศาลครอบครัวที่ใช้ต่างประเทศ เช่น คำพิพากษาหย่า คำสั่งกำหนดอำนาจปกครองบุตร หรือคำสั่งรับรองบุตร ต้องใช้ฉบับคัดรับรองจากศาลพร้อมประทับตราคดีถึงที่สุด แล้วจึงแปลพร้อมคำรับรองการแปล โดยทั่วไปงานแปลใช้เวลา 2–5 วันทำการตามความยาว และหากต้องรับรองกงสุลและสถานทูตต่อ ให้เผื่อเพิ่ม 5–10 วันทำการ",
      audience: "ผู้ที่ต้องยื่นสถานะสมรสหรืออำนาจปกครองบุตรกับหน่วยงานต่างประเทศ ทั้งการจดทะเบียนสมรสใหม่ ขอวีซ่าติดตามครอบครัว และการย้ายถิ่นฐาน",
      facts: [
        { label: "ต้นฉบับที่ใช้ได้", value: "ฉบับคัดรับรองจากศาล พร้อมหมายเหตุคดีถึงที่สุดเมื่อปลายทางร้องขอ" },
        { label: "ระยะเวลาแปล", value: "2–5 วันทำการตามจำนวนหน้า" },
        { label: "รับรองกงสุลและสถานทูต", value: "เพิ่ม 5–10 วันทำการ" },
        { label: "ภาษาที่รองรับ", value: "อังกฤษ จีน ญี่ปุ่น เกาหลี เยอรมัน ฝรั่งเศส และภาษาอื่นตามปลายทาง" },
        { label: "รูปแบบส่งมอบ", value: "เย็บชุดต้นฉบับ–คำแปล–คำรับรอง พร้อมไฟล์สแกน" },
      ],
      documents: [
        "คำพิพากษาหรือคำสั่งศาลฉบับคัดรับรอง",
        "หนังสือรับรองคดีถึงที่สุด หากปลายทางกำหนด",
        "ทะเบียนหย่าหรือทะเบียนสมรส แล้วแต่กรณี",
        "สูติบัตรบุตรและทะเบียนบ้าน สำหรับคดีอำนาจปกครอง",
        "หนังสือเดินทางของคู่ความ เพื่ออ้างอิงการสะกดชื่อ",
      ],
      steps: [
        { title: "ตรวจความครบถ้วนของสำนวน", detail: "ตรวจว่าเอกสารเป็นฉบับคัดรับรองและมีหน้าครบทุกแผ่นตามเลขหน้า", who: "ที่ปรึกษาเอกสาร", eta: "ภายในวันเดียว" },
        { title: "จัดคำศัพท์กฎหมาย", detail: "วางคำศัพท์กฎหมายครอบครัวและชื่อเฉพาะให้ตรงกับหนังสือเดินทางทุกจุด", who: "นักแปลกฎหมาย", eta: "1 วันทำการ" },
        { title: "แปลและทวนสอบ", detail: "แปลแล้วให้ผู้ทวนสอบอีกคนตรวจตัวเลข วันที่ และเลขคดี", who: "ทีมแปลและผู้ทวนสอบ", eta: "2–5 วันทำการ" },
        { title: "ออกคำรับรองการแปล", detail: "ออกคำรับรองการแปลและรับรองโดยทนายเมื่อปลายทางกำหนด", who: "บริษัทแปลและทนาย", eta: "1 วันทำการ" },
        { title: "รับรองกงสุลและสถานทูต", detail: "ยื่นตามลำดับที่ปลายทางกำหนดจนครบสาย", who: "ทีมเดินเอกสาร", eta: "5–10 วันทำการ" },
      ],
      pitfalls: [
        "ใช้สำเนาถ่ายเอกสารแทนฉบับคัดรับรองจากศาล",
        "ขาดหนังสือรับรองคดีถึงที่สุดทั้งที่ปลายทางบังคับ",
        "สะกดชื่อคู่ความไม่ตรงกับหนังสือเดินทาง",
        "แปลเลขคดีหรือวันที่ผิดเพียงหลักเดียว ทำให้ถูกตีกลับทั้งชุด",
        "ยื่นสถานทูตก่อนกรมการกงสุล",
      ],
      compare: [
        { option: "แปลพร้อมคำรับรองการแปล", useWhen: "ปลายทางรับคำรับรองบริษัทแปล", time: "2–5 วันทำการ", note: "เร็วที่สุด" },
        { option: "แปล + รับรองทนาย", useWhen: "ปลายทางขอลายมือชื่อทนายกำกับ", time: "3–6 วันทำการ", note: "เพิ่มน้ำหนักความน่าเชื่อถือ" },
        { option: "ครบสายถึงสถานทูต", useWhen: "ใช้กับนายทะเบียนหรือศาลต่างประเทศ", time: "7–15 วันทำการ", note: "ขึ้นกับคิวแต่ละสถานทูต" },
      ],
      faqs: [
        { q: "ต้องใช้คำพิพากษาฉบับเต็มหรือย่อ", a: "ปลายทางส่วนใหญ่ขอฉบับเต็มพร้อมทุกหน้า การส่งเฉพาะหน้าสรุปมักถูกตีกลับ" },
        { q: "คดีถึงที่สุดแล้วต้องมีหนังสือยืนยันไหม", a: "หลายประเทศบังคับ ควรขอหนังสือรับรองคดีถึงที่สุดจากศาลไปพร้อมกัน" },
        { q: "แปลเป็นภาษาที่ไม่ใช่อังกฤษได้ไหม", a: "ได้ เรามีนักแปลกฎหมายหลายภาษา และจัดผู้ทวนสอบแยกทุกภาษา" },
        { q: "ชื่อในคำพิพากษาสะกดต่างจากหนังสือเดินทาง ทำอย่างไร", a: "แปลตามต้นฉบับแล้วแนบหมายเหตุการสะกด พร้อมแนะนำให้ยื่นเอกสารยืนยันตัวบุคคลประกอบ" },
        { q: "ใช้เวลานานที่สุดในขั้นตอนไหน", a: "โดยทั่วไปคือคิวของสถานทูตปลายทาง ไม่ใช่ขั้นแปล" },
        { q: "จำเป็นต้องรับรองกงสุลเสมอไหม", a: "ไม่เสมอไป ขึ้นกับหน่วยงานปลายทาง เราตรวจให้ก่อนเริ่มงาน" },
        { q: "รับงานเร่งด่วนไหม", a: "รับ โดยแบ่งงานขนานแต่ยังคงขั้นทวนสอบไว้เสมอ" },
        { q: "เก็บเอกสารเป็นความลับอย่างไร", a: "ทำ NDA จำกัดผู้เข้าถึงเท่าที่จำเป็น และลบไฟล์ทำงานตามกำหนดที่ตกลงกัน" },
      ],
      consultant:
        "คดีครอบครัวมีรายละเอียดที่พลาดไม่ได้ ทีมงานกว่า 15 ปีจะตรวจสำนวน คำศัพท์ และลำดับการรับรองให้ก่อนเริ่ม เพื่อไม่ให้เสียเวลากลับไปคัดเอกสารใหม่ หากไม่อยากเดินเรื่องเอง เราดูแลตั้งแต่คัดเอกสารจนถึงสถานทูต",
      related: [
        ["แปลเอกสารรับรอง", "/translation"],
        ["รับรองเอกสารกงสุลและสถานทูต", "/mfa-legalisation"],
        ["บริการรับรองเอกสารโดยทนาย Notary", "/notary"],
      ],
    },
    en: {
      title: "Family court document translation and legalisation",
      h1: "Family court judgments and orders: certified translation and full legalisation",
      metaTitle: "Family Court Document Translation and Legalisation Thailand",
      metaDesc:
        "Certified translation of divorce judgments, custody orders and family court records, with translation certificates, notarial certification and consular legalisation handled in one chain.",
      answer:
        "Family court documents used abroad, such as divorce judgments, custody orders or legitimation orders, must be court-certified copies, with a finality endorsement where the destination requires it, before translation with a translation certificate. Translation normally takes two to five working days; add five to ten working days for consular and embassy legalisation.",
      audience: "People proving marital status or custody to foreign authorities for remarriage, family visas or relocation.",
      facts: [
        { label: "Acceptable source", value: "Court-certified copy, with finality endorsement when required" },
        { label: "Translation time", value: "2–5 working days depending on page count" },
        { label: "Consular and embassy", value: "Add 5–10 working days" },
        { label: "Languages", value: "English, Chinese, Japanese, Korean, German, French and others on request" },
        { label: "Delivery format", value: "Original, translation and certificate bound together, plus scans" },
      ],
      documents: [
        "Court-certified judgment or order",
        "Finality certificate if the destination requires it",
        "Divorce or marriage registration record as applicable",
        "Children's birth certificates and household registration for custody matters",
        "Passports of the parties for name spelling",
      ],
      steps: [
        { title: "Check the file is complete", detail: "Confirm the copy is court-certified and every numbered page is present.", who: "Document consultant", eta: "Same day" },
        { title: "Set legal terminology", detail: "Fix family-law terminology and proper names against passports.", who: "Legal translator", eta: "1 working day" },
        { title: "Translate and review", detail: "A second reviewer checks figures, dates and case numbers.", who: "Translation and review team", eta: "2–5 working days" },
        { title: "Issue certification", detail: "Issue the translation certificate and add notarial certification where required.", who: "Translation company and attorney", eta: "1 working day" },
        { title: "Consular and embassy legalisation", detail: "File in the order the destination prescribes until the chain is complete.", who: "Filing team", eta: "5–10 working days" },
      ],
      pitfalls: [
        "Submitting a photocopy instead of a court-certified copy",
        "Missing the finality certificate where it is mandatory",
        "Party names spelled differently from the passport",
        "A single wrong digit in a case number or date rejecting the whole set",
        "Filing with the embassy before the Department of Consular Affairs",
      ],
      compare: [
        { option: "Translation with certificate", useWhen: "The destination accepts a company certificate", time: "2–5 working days", note: "Fastest option" },
        { option: "Translation plus notarial certification", useWhen: "An attorney signature is required", time: "3–6 working days", note: "Adds evidentiary weight" },
        { option: "Full chain to the embassy", useWhen: "Filing with a foreign registrar or court", time: "7–15 working days", note: "Depends on embassy queues" },
      ],
      faqs: [
        { q: "Full judgment or summary?", a: "Most destinations require the complete judgment with all pages; summary pages alone are usually rejected." },
        { q: "Is a finality certificate needed?", a: "Many countries require it, so request it from the court at the same time." },
        { q: "Can you translate into languages other than English?", a: "Yes, with legal translators and a separate reviewer for each language." },
        { q: "Names differ from the passport. What then?", a: "We translate faithfully and attach a spelling note, and advise on supporting identity evidence." },
        { q: "Which step takes longest?", a: "Usually the destination embassy queue, not the translation." },
        { q: "Is consular legalisation always required?", a: "Not always; it depends on the receiving body, which we check before starting." },
        { q: "Do you handle rush work?", a: "Yes, with parallel teams while keeping the review pass." },
        { q: "How is confidentiality maintained?", a: "NDA, need-to-know access and scheduled deletion of working files." },
      ],
      consultant:
        "Family matters leave no room for detail errors. Our 15+ year team checks the file, terminology and legalisation order before work starts, so you do not have to return to the court for new copies. We can run the whole chain for you.",
      related: [
        ["Certified translation", "/en/translation"],
        ["MFA and embassy legalisation", "/en/mfa-legalisation"],
        ["Notary Public service", "/en/notary"],
      ],
    },
  },
  {
    slug: "professional-licence-certification",
    cluster: "certified",
    th: {
      title: "รับรองและแปลใบอนุญาตประกอบวิชาชีพสำหรับใช้ต่างประเทศ",
      h1: "รับรองใบอนุญาตประกอบวิชาชีพ แพทย์ พยาบาล วิศวกร ครู และสายอาชีพอื่น",
      metaTitle: "รับรองใบอนุญาตประกอบวิชาชีพ แปลรับรอง และรับรองกงสุล",
      metaDesc:
        "แปลและรับรองใบอนุญาตประกอบวิชาชีพ ใบประกอบโรคศิลปะ ใบ ก.ว. ใบอนุญาตครู พร้อมหนังสือรับรองสถานะจากสภาวิชาชีพ และรับรองกงสุล–สถานทูตครบสาย",
      answer:
        "การใช้ใบอนุญาตประกอบวิชาชีพไทยในต่างประเทศมักต้องใช้สามชิ้นควบกัน คือใบอนุญาตฉบับจริงหรือฉบับคัดรับรอง หนังสือรับรองสถานะใบอนุญาตจากสภาวิชาชีพ และคำแปลรับรอง โดยทั่วไปงานแปลและรับรองใช้เวลา 3–7 วันทำการ ส่วนหนังสือรับรองจากสภาวิชาชีพขึ้นกับระเบียบของแต่ละสภา และหากปลายทางต้องการรับรองกงสุลต่อ ให้เผื่อเพิ่ม 5–10 วันทำการ",
      audience: "แพทย์ พยาบาล เภสัชกร วิศวกร สถาปนิก ครู และผู้ประกอบวิชาชีพที่ยื่นขอขึ้นทะเบียนหรือทำงานในต่างประเทศ",
      facts: [
        { label: "เอกสารหลัก", value: "ใบอนุญาต ใบรับรองสถานะจากสภาวิชาชีพ และคำแปลรับรอง" },
        { label: "ระยะเวลาแปลและรับรอง", value: "3–7 วันทำการ" },
        { label: "รับรองกงสุลและสถานทูต", value: "เพิ่ม 5–10 วันทำการ" },
        { label: "หน่วยงานที่มักต้องติดต่อ", value: "สภาวิชาชีพต้นสังกัด และหน่วยงานขึ้นทะเบียนปลายทาง" },
        { label: "รูปแบบที่ปลายทางนิยม", value: "ส่งตรงจากสภาวิชาชีพหรือชุดเอกสารที่รับรองครบสาย" },
      ],
      documents: [
        "ใบอนุญาตประกอบวิชาชีพฉบับจริงหรือฉบับคัดรับรอง",
        "หนังสือรับรองสถานะใบอนุญาต (Certificate of Good Standing) จากสภาวิชาชีพ",
        "ปริญญาบัตรและใบแสดงผลการศึกษา",
        "หนังสือเดินทาง เพื่ออ้างอิงการสะกดชื่อ",
        "หนังสือรับรองประสบการณ์ทำงาน หากปลายทางกำหนด",
      ],
      steps: [
        { title: "ตรวจข้อกำหนดปลายทาง", detail: "ตรวจว่าปลายทางรับชุดเอกสารแบบใด และต้องส่งตรงจากสภาวิชาชีพหรือไม่", who: "ที่ปรึกษาเอกสาร", eta: "1 วันทำการ" },
        { title: "ขอหนังสือรับรองสถานะ", detail: "ยื่นคำขอหนังสือรับรองสถานะใบอนุญาตกับสภาวิชาชีพต้นสังกัด", who: "เจ้าของใบอนุญาตพร้อมทีมสนับสนุน", eta: "ตามระเบียบแต่ละสภา" },
        { title: "แปลและทวนสอบ", detail: "แปลชุดเอกสารพร้อมคำศัพท์วิชาชีพและตรวจโดยผู้ทวนสอบอีกคน", who: "ทีมแปล", eta: "3–7 วันทำการ" },
        { title: "รับรองการแปลและทนาย", detail: "ออกคำรับรองการแปล และรับรองโดยทนายเมื่อปลายทางกำหนด", who: "บริษัทแปลและทนาย", eta: "1 วันทำการ" },
        { title: "รับรองกงสุลและสถานทูต", detail: "ยื่นตามลำดับจนครบสายเพื่อให้ปลายทางรับรองเอกสาร", who: "ทีมเดินเอกสาร", eta: "5–10 วันทำการ" },
      ],
      pitfalls: [
        "ยื่นเฉพาะใบอนุญาตโดยไม่มีหนังสือรับรองสถานะ",
        "หนังสือรับรองสถานะหมดอายุตามเกณฑ์ปลายทาง เช่น เกิน 3 เดือน",
        "ชื่อวิชาชีพแปลไม่ตรงกับระบบขึ้นทะเบียนปลายทาง",
        "ขาดใบแสดงผลการศึกษาที่ปลายทางใช้เทียบหลักสูตร",
        "ไม่ตรวจว่าปลายทางบังคับให้ส่งตรงจากสภาวิชาชีพ",
      ],
      compare: [
        { option: "แปลรับรองอย่างเดียว", useWhen: "ปลายทางรับคำรับรองบริษัทแปล", time: "3–7 วันทำการ", note: "เร็วที่สุด" },
        { option: "แปล + หนังสือรับรองสถานะ", useWhen: "หน่วยงานขึ้นทะเบียนปลายทางบังคับ", time: "ขึ้นกับคิวสภาวิชาชีพ", note: "ชุดมาตรฐานที่ใช้บ่อยที่สุด" },
        { option: "ครบสายถึงสถานทูต", useWhen: "ต้องยื่นกับหน่วยงานรัฐต่างประเทศ", time: "10–20 วันทำการ", note: "เผื่อคิวสถานทูต" },
      ],
      faqs: [
        { q: "หนังสือรับรองสถานะคืออะไร", a: "คือหนังสือจากสภาวิชาชีพยืนยันว่าใบอนุญาตยังใช้ได้และไม่มีประวัติถูกลงโทษทางวินัย" },
        { q: "แปลชื่อวิชาชีพให้ตรงระบบปลายทางอย่างไร", a: "ใช้คำที่ระบบขึ้นทะเบียนปลายทางใช้จริง และแนบหมายเหตุอธิบายคำไทยต้นฉบับ" },
        { q: "ต้องแปลใบแสดงผลการศึกษาด้วยไหม", a: "หลายประเทศต้องใช้เพื่อเทียบหลักสูตร ควรเตรียมไว้ตั้งแต่ต้น" },
        { q: "ใบอนุญาตหมดอายุยื่นได้ไหม", a: "ควรต่ออายุก่อน เพราะปลายทางส่วนใหญ่ตรวจสถานะ ณ วันยื่น" },
        { q: "ใช้กับการสมัครงานต่างประเทศได้ไหม", a: "ได้ นายจ้างมักขอชุดเดียวกับที่หน่วยงานขึ้นทะเบียนใช้" },
        { q: "รับรองกงสุลจำเป็นไหม", a: "ขึ้นกับปลายทาง เราตรวจให้ก่อนเริ่มเพื่อไม่ให้ทำเกินจำเป็น" },
        { q: "เอกสารต้องส่งตรงจากสภาวิชาชีพหรือไม่", a: "บางประเทศบังคับ เช่น ระบบขึ้นทะเบียนสายสุขภาพหลายแห่ง เราช่วยประสานรูปแบบการส่ง" },
        { q: "ใช้เวลารวมทั้งหมดเท่าไร", a: "โดยทั่วไป 2–4 สัปดาห์ ขึ้นกับคิวสภาวิชาชีพและสถานทูต" },
      ],
      consultant:
        "งานขึ้นทะเบียนวิชาชีพต่างประเทศแพ้ทางรายละเอียดเล็ก ๆ ทีมงานกว่า 15 ปีจะไล่เช็กลิสต์ปลายทางให้ครบก่อนเริ่ม และถ้าไม่อยากเดินเรื่องเอง เราประสานสภาวิชาชีพ แปล และรับรองให้ครบสาย",
      related: [
        ["แปลเอกสารรับรอง", "/translation"],
        ["รับรองเอกสารกงสุลและสถานทูต", "/mfa-legalisation"],
        ["บริการรับรองเอกสารโดยทนาย Notary", "/notary"],
      ],
    },
    en: {
      title: "Professional licence certification and translation",
      h1: "Professional licence certification for doctors, nurses, engineers and teachers",
      metaTitle: "Professional Licence Certification and Certified Translation",
      metaDesc:
        "Translate and certify Thai professional licences, medical and engineering registrations and teaching licences, with council good-standing letters and consular legalisation handled end to end.",
      answer:
        "Using a Thai professional licence abroad usually requires three items together: the licence or a certified copy, a good-standing letter from the professional council, and a certified translation. Translation and certification normally take three to seven working days, the council letter follows each council's own procedure, and consular legalisation adds five to ten working days.",
      audience: "Doctors, nurses, pharmacists, engineers, architects, teachers and other licensed professionals registering or working abroad.",
      facts: [
        { label: "Core documents", value: "Licence, council good-standing letter and certified translation" },
        { label: "Translation and certification", value: "3–7 working days" },
        { label: "Consular and embassy", value: "Add 5–10 working days" },
        { label: "Bodies involved", value: "Your Thai professional council and the destination registration board" },
        { label: "Preferred format", value: "Direct dispatch from the council, or a fully legalised document set" },
      ],
      documents: [
        "Original or certified copy of the professional licence",
        "Certificate of good standing from the professional council",
        "Degree certificate and academic transcript",
        "Passport for name spelling",
        "Employment verification letters if required",
      ],
      steps: [
        { title: "Check destination rules", detail: "Confirm the accepted document set and whether direct council dispatch is mandatory.", who: "Document consultant", eta: "1 working day" },
        { title: "Request good standing", detail: "File the good-standing request with your Thai professional council.", who: "Licence holder with our support", eta: "Per council procedure" },
        { title: "Translate and review", detail: "Translate with correct professional terminology and a second reviewer.", who: "Translation team", eta: "3–7 working days" },
        { title: "Certify the translation", detail: "Issue the translation certificate and add notarial certification if required.", who: "Translation company and attorney", eta: "1 working day" },
        { title: "Consular and embassy legalisation", detail: "Complete the chain so the destination board accepts the set.", who: "Filing team", eta: "5–10 working days" },
      ],
      pitfalls: [
        "Submitting the licence without a good-standing letter",
        "A good-standing letter older than the destination allows, often three months",
        "Profession titles translated differently from the destination register",
        "Missing transcripts needed for curriculum comparison",
        "Overlooking a mandatory direct-dispatch requirement",
      ],
      compare: [
        { option: "Certified translation only", useWhen: "The destination accepts a company certificate", time: "3–7 working days", note: "Fastest option" },
        { option: "Translation plus good-standing letter", useWhen: "Required by the destination board", time: "Depends on council queue", note: "The most common set" },
        { option: "Full chain to the embassy", useWhen: "Filing with a foreign government body", time: "10–20 working days", note: "Allow for embassy queues" },
      ],
      faqs: [
        { q: "What is a good-standing letter?", a: "A council letter confirming the licence is valid and free of disciplinary findings." },
        { q: "How do you match profession titles?", a: "We use the destination register's own wording and annotate the Thai source term." },
        { q: "Are transcripts needed?", a: "Many countries use them for curriculum comparison, so prepare them early." },
        { q: "Can an expired licence be used?", a: "Renew first; most boards verify status on the filing date." },
        { q: "Is this accepted for overseas job applications?", a: "Yes, employers usually ask for the same set the registration board uses." },
        { q: "Is consular legalisation required?", a: "It depends on the destination; we check before starting so nothing unnecessary is done." },
        { q: "Must the council send documents directly?", a: "Some health registers require it. We help arrange the accepted dispatch route." },
        { q: "What is the total timeline?", a: "Typically two to four weeks depending on council and embassy queues." },
      ],
      consultant:
        "Overseas registration usually fails on small details. Our 15+ year team walks the destination checklist before work begins, and can coordinate the council, translation and legalisation for you.",
      related: [
        ["Certified translation", "/en/translation"],
        ["MFA and embassy legalisation", "/en/mfa-legalisation"],
        ["Notary Public service", "/en/notary"],
      ],
    },
  },
  {
    slug: "intercountry-adoption-documents",
    cluster: "certified",
    th: {
      title: "จัดชุดเอกสารรับบุตรบุญธรรมข้ามประเทศ",
      h1: "เอกสารรับบุตรบุญธรรมข้ามประเทศ แปล รับรอง และเดินเรื่องครบสาย",
      metaTitle: "เอกสารรับบุตรบุญธรรมข้ามประเทศ แปลรับรองและรับรองกงสุล",
      metaDesc:
        "จัดชุดเอกสารรับบุตรบุญธรรมข้ามประเทศ ทั้งคำสั่งศาล ทะเบียนรับบุตรบุญธรรม เอกสารผู้รับ และการแปลรับรอง พร้อมรับรองกงสุลและสถานทูตครบสาย",
      answer:
        "งานรับบุตรบุญธรรมข้ามประเทศต้องประสานสามฝ่าย คือหน่วยงานคุ้มครองเด็กของไทย ศาลหรือนายทะเบียน และหน่วยงานปลายทาง ชุดเอกสารมักประกอบด้วยคำสั่งอนุญาต ทะเบียนรับบุตรบุญธรรม สูติบัตรเด็ก เอกสารสถานะของผู้รับ และรายงานประเมินครอบครัว โดยงานแปลและรับรองมักใช้เวลา 5–10 วันทำการ ส่วนขั้นตอนของหน่วยงานเป็นไปตามระเบียบราชการ",
      audience: "ครอบครัวไทย–ต่างชาติ และผู้รับบุตรบุญธรรมที่ต้องยื่นเอกสารกับหน่วยงานทั้งสองประเทศ",
      facts: [
        { label: "ลักษณะงาน", value: "งานเอกสารประกอบกระบวนการทางปกครองและศาล ไม่ใช่การเร่งอนุมัติ" },
        { label: "ระยะเวลาแปลและรับรอง", value: "5–10 วันทำการต่อชุด" },
        { label: "รับรองกงสุลและสถานทูต", value: "เพิ่ม 5–10 วันทำการ" },
        { label: "ความถูกต้องของชื่อ", value: "ชื่อเด็กและผู้รับต้องตรงกับหนังสือเดินทางทุกฉบับ" },
        { label: "อายุเอกสาร", value: "ปลายทางหลายแห่งรับเอกสารที่ออกไม่เกิน 3–6 เดือน" },
      ],
      documents: [
        "คำสั่งศาลหรือเอกสารอนุญาตรับบุตรบุญธรรม",
        "ทะเบียนรับบุตรบุญธรรม (คร.14) ตามกรณี",
        "สูติบัตรเด็กและทะเบียนบ้าน",
        "หนังสือเดินทางของผู้รับบุตรบุญธรรมและเด็ก",
        "รายงานประเมินครอบครัวและหนังสือรับรองรายได้ตามที่ปลายทางกำหนด",
      ],
      steps: [
        { title: "วางแผนชุดเอกสารสองประเทศ", detail: "แยกรายการเอกสารฝั่งไทยและฝั่งปลายทาง พร้อมลำดับการรับรองที่ถูกต้อง", who: "ที่ปรึกษาเอกสาร", eta: "1–2 วันทำการ" },
        { title: "รวบรวมและคัดเอกสารใหม่", detail: "คัดเอกสารราชการฉบับใหม่ให้อยู่ในกรอบอายุที่ปลายทางรับ", who: "ทีมเดินเอกสาร", eta: "ตามคิวหน่วยงาน" },
        { title: "แปลและทวนสอบ", detail: "แปลชุดเอกสารพร้อมตรวจชื่อ วันที่ และเลขเอกสารโดยผู้ทวนสอบอีกคน", who: "ทีมแปล", eta: "5–10 วันทำการ" },
        { title: "รับรองการแปลและทนาย", detail: "ออกคำรับรองการแปลและรับรองโดยทนายตามที่ปลายทางกำหนด", who: "บริษัทแปลและทนาย", eta: "1–2 วันทำการ" },
        { title: "รับรองกงสุลและสถานทูต", detail: "ยื่นกรมการกงสุลและสถานทูตปลายทางตามลำดับ", who: "ทีมเดินเอกสาร", eta: "5–10 วันทำการ" },
      ],
      pitfalls: [
        "เอกสารราชการเก่าเกินกรอบอายุที่ปลายทางรับ",
        "สะกดชื่อเด็กในสูติบัตรกับหนังสือเดินทางไม่ตรงกัน",
        "ขาดรายงานประเมินครอบครัวที่ปลายทางบังคับ",
        "แปลชื่อหน่วยงานไทยไม่ตรงกับที่ปลายทางคุ้นเคย",
        "ยื่นสถานทูตก่อนกรมการกงสุล ทำให้ต้องเริ่มใหม่",
      ],
      compare: [
        { option: "เฉพาะงานแปลรับรอง", useWhen: "ลูกค้าเดินเรื่องหน่วยงานเอง", time: "5–10 วันทำการ", note: "ประหยัดเวลาลูกค้าในส่วนภาษา" },
        { option: "แปล + รับรองกงสุล", useWhen: "ปลายทางขอเอกสารรับรองจากรัฐไทย", time: "10–15 วันทำการ", note: "ชุดที่ใช้บ่อย" },
        { option: "ดูแลครบสายสองประเทศ", useWhen: "ไม่ต้องการประสานเอง", time: "ตามคิวหน่วยงานทั้งสองฝั่ง", note: "มีผู้ประสานงานเดียวตลอดเรื่อง" },
      ],
      faqs: [
        { q: "บริการนี้ช่วยเร่งการอนุมัติได้ไหม", a: "ไม่ได้ และเราไม่รับปากผลการพิจารณา เราดูแลให้ชุดเอกสารถูกต้องครบถ้วนเพื่อลดการตีกลับ" },
        { q: "ต้องแปลเอกสารฝั่งต่างประเทศเป็นไทยด้วยไหม", a: "โดยทั่วไปต้อง เพราะหน่วยงานไทยต้องใช้ฉบับภาษาไทยพร้อมคำรับรอง" },
        { q: "รายงานประเมินครอบครัวใครออก", a: "หน่วยงานสังคมสงเคราะห์หรือองค์กรที่ปลายทางรับรอง เราช่วยตรวจรูปแบบและการแปล" },
        { q: "เด็กเดินทางออกนอกประเทศได้เมื่อใด", a: "เมื่อกระบวนการทางกฎหมายและเอกสารเดินทางครบตามที่หน่วยงานทั้งสองประเทศกำหนด" },
        { q: "ชื่อสกุลเด็กเปลี่ยนหรือไม่", a: "ขึ้นกับคำสั่งและระเบียบทะเบียนราษฎร หากเปลี่ยนต้องปรับเอกสารทั้งชุดให้สอดคล้อง" },
        { q: "ใช้เวลาทั้งหมดเท่าไร", a: "ส่วนเอกสารมักอยู่ที่ 3–6 สัปดาห์ ส่วนกระบวนการหน่วยงานขึ้นกับระเบียบราชการ" },
        { q: "เอกสารเป็นความลับหรือไม่", a: "เป็นความลับ เราจำกัดผู้เข้าถึงและลบไฟล์ทำงานตามกำหนดที่ตกลงกัน" },
        { q: "รองรับภาษาอะไรบ้าง", a: "อังกฤษและภาษายุโรปหลัก รวมถึงจีน ญี่ปุ่น เกาหลี ตามประเทศปลายทาง" },
      ],
      consultant:
        "เรื่องรับบุตรบุญธรรมข้ามประเทศมีเอกสารสองฝั่งที่ต้องสอดคล้องกันทุกจุด ทีมที่ปรึกษากว่า 15 ปีจะทำเช็กลิสต์รวมให้ทั้งสองประเทศ ตรวจอายุเอกสาร และคุมลำดับการรับรอง เพื่อให้ครอบครัวโฟกัสกับเรื่องสำคัญกว่าการวิ่งเอกสาร",
      related: [
        ["แปลเอกสารรับรอง", "/translation"],
        ["รับรองเอกสารกงสุลและสถานทูต", "/mfa-legalisation"],
        ["กรณีศึกษาบริการ", "/case-study"],
      ],
    },
    en: {
      title: "Intercountry adoption document packages",
      h1: "Intercountry adoption documents: translation, certification and full legalisation",
      metaTitle: "Intercountry Adoption Document Translation and Legalisation",
      metaDesc:
        "Prepare intercountry adoption document sets including court orders, adoption registration, child records and home study reports, with certified translation and consular legalisation.",
      answer:
        "Intercountry adoption paperwork spans three sides: the Thai child-protection authority, the court or registrar, and the destination authority. Sets usually include the approval order, adoption registration, the child's birth certificate, the adopters' status documents and a home study report. Translation and certification typically take five to ten working days, while authority timelines follow their own procedures.",
      audience: "Thai-foreign families and adoptive parents filing with authorities in both countries.",
      facts: [
        { label: "Scope", value: "Document work supporting administrative and court procedures, not expedited approval" },
        { label: "Translation and certification", value: "5–10 working days per set" },
        { label: "Consular and embassy", value: "Add 5–10 working days" },
        { label: "Name consistency", value: "Child and adopter names must match passports across every document" },
        { label: "Document age", value: "Many destinations accept records issued within 3–6 months" },
      ],
      documents: [
        "Court order or adoption approval",
        "Adoption registration record where applicable",
        "Child's birth certificate and household registration",
        "Passports of the adopters and the child",
        "Home study report and income evidence as required",
      ],
      steps: [
        { title: "Plan both document sets", detail: "Split the Thai and destination lists and fix the correct legalisation order.", who: "Document consultant", eta: "1–2 working days" },
        { title: "Collect fresh certified copies", detail: "Re-issue official records so they fall within the destination's accepted age.", who: "Filing team", eta: "Per authority queue" },
        { title: "Translate and review", detail: "Translate with a second reviewer checking names, dates and reference numbers.", who: "Translation team", eta: "5–10 working days" },
        { title: "Certify the translation", detail: "Issue the translation certificate with notarial certification where required.", who: "Translation company and attorney", eta: "1–2 working days" },
        { title: "Consular and embassy legalisation", detail: "File with the Department of Consular Affairs and then the destination embassy.", who: "Filing team", eta: "5–10 working days" },
      ],
      pitfalls: [
        "Official records older than the destination accepts",
        "Child's name spelled differently on the birth certificate and passport",
        "Missing a mandatory home study report",
        "Thai authority names translated inconsistently",
        "Filing with the embassy before the Department of Consular Affairs",
      ],
      compare: [
        { option: "Translation and certification only", useWhen: "You handle the authorities yourself", time: "5–10 working days", note: "Covers the language side" },
        { option: "Translation plus consular legalisation", useWhen: "The destination requires Thai state authentication", time: "10–15 working days", note: "The common set" },
        { option: "Full two-country coordination", useWhen: "You prefer a single point of contact", time: "Per both authorities' queues", note: "One coordinator throughout" },
      ],
      faqs: [
        { q: "Can you speed up approval?", a: "No, and we make no promises about outcomes. We keep the document set correct so it is not rejected." },
        { q: "Do foreign documents need Thai translation?", a: "Usually yes, since Thai authorities require certified Thai versions." },
        { q: "Who issues the home study report?", a: "A social welfare body or an agency the destination recognises; we review format and translation." },
        { q: "When can the child travel?", a: "Once the legal process and travel documents are complete under both countries' rules." },
        { q: "Does the child's surname change?", a: "It depends on the order and civil registration rules; if it changes, the whole set must be aligned." },
        { q: "How long does it take overall?", a: "The document side is usually three to six weeks; authority procedures follow their own timelines." },
        { q: "Is the file confidential?", a: "Yes, with restricted access and scheduled deletion of working files." },
        { q: "Which languages are covered?", a: "English and major European languages, plus Chinese, Japanese and Korean." },
      ],
      consultant:
        "Adoption files must align perfectly on both sides. Our 15+ year advisory team builds a combined checklist, watches document ages and controls the legalisation order so families can focus on what matters.",
      related: [
        ["Certified translation", "/en/translation"],
        ["MFA and embassy legalisation", "/en/mfa-legalisation"],
        ["Service case studies", "/en/case-study"],
      ],
    },
  },
  {
    slug: "trademark-ip-document-certification",
    cluster: "certified",
    th: {
      title: "รับรองและแปลเอกสารเครื่องหมายการค้าและทรัพย์สินทางปัญญา",
      h1: "เอกสารเครื่องหมายการค้า สิทธิบัตร และลิขสิทธิ์ แปลรับรองและรับรองครบสาย",
      metaTitle: "แปลรับรองเอกสารเครื่องหมายการค้าและทรัพย์สินทางปัญญา",
      metaDesc:
        "แปลและรับรองหนังสือมอบอำนาจ IP หนังสือโอนสิทธิ์ เอกสารสิทธิบัตรและเครื่องหมายการค้า พร้อมรับรองทนายและรับรองกงสุล–สถานทูตสำหรับยื่นในและต่างประเทศ",
      answer:
        "งานยื่นเครื่องหมายการค้าหรือสิทธิบัตรข้ามประเทศมักต้องใช้หนังสือมอบอำนาจ (Power of Attorney) หนังสือโอนสิทธิ์ (Deed of Assignment) และเอกสารนิติบุคคลของผู้ยื่น พร้อมคำแปลรับรอง โดยงานแปลและรับรองทนายมักใช้เวลา 2–5 วันทำการ และหากนายทะเบียนปลายทางต้องการรับรองกงสุลต่อ ให้เผื่อเพิ่ม 5–10 วันทำการ",
      audience: "บริษัท ตัวแทนสิทธิบัตร และสำนักงานกฎหมายที่ยื่นคำขอ IP ในไทยหรือต่างประเทศ",
      facts: [
        { label: "เอกสารที่พบบ่อย", value: "POA, Deed of Assignment, หนังสือรับรองบริษัท และเอกสารลำดับสิทธิ" },
        { label: "ระยะเวลาแปลและรับรอง", value: "2–5 วันทำการ" },
        { label: "รับรองกงสุลและสถานทูต", value: "เพิ่ม 5–10 วันทำการ" },
        { label: "ความสำคัญของวันที่", value: "วันที่ในเอกสารต้องสอดคล้องกับวันยื่นและวันอ้างสิทธิ" },
        { label: "ความสม่ำเสมอของชื่อ", value: "ชื่อผู้ขอต้องตรงกับทะเบียนนิติบุคคลทุกฉบับ" },
      ],
      documents: [
        "หนังสือมอบอำนาจตามแบบที่นายทะเบียนปลายทางกำหนด",
        "หนังสือโอนสิทธิ์ระหว่างผู้ประดิษฐ์กับผู้ขอ หากมี",
        "หนังสือรับรองบริษัทฉบับคัดใหม่",
        "รายละเอียดคำขอ เช่น เลขคำขอ ชั้นสินค้า หรือรายการข้อถือสิทธิ",
        "หนังสือเดินทางหรือบัตรประชาชนของผู้ลงนาม",
      ],
      steps: [
        { title: "ตรวจแบบฟอร์มปลายทาง", detail: "ตรวจว่าปลายทางใช้แบบ POA เฉพาะหรือไม่ และต้องรับรองระดับใด", who: "ที่ปรึกษาเอกสาร", eta: "ภายในวันเดียว" },
        { title: "จัดคำศัพท์เทคนิค", detail: "วางคำศัพท์เฉพาะทางและชื่อทางเทคนิคให้สอดคล้องทั้งชุด", who: "นักแปลสายเทคนิค", eta: "1 วันทำการ" },
        { title: "แปลและทวนสอบ", detail: "แปลแล้วตรวจซ้ำที่เลขคำขอ วันที่ และรายการข้อถือสิทธิ", who: "ทีมแปลและผู้ทวนสอบ", eta: "2–5 วันทำการ" },
        { title: "ลงนามและรับรองทนาย", detail: "ผู้มีอำนาจลงนามต่อหน้าทนาย และทนายออกคำรับรอง", who: "ทนายผู้ทำคำรับรอง", eta: "1 วันทำการ" },
        { title: "รับรองกงสุลและสถานทูต", detail: "ยื่นตามลำดับหากนายทะเบียนปลายทางกำหนด", who: "ทีมเดินเอกสาร", eta: "5–10 วันทำการ" },
      ],
      pitfalls: [
        "ใช้แบบ POA ทั่วไปทั้งที่นายทะเบียนปลายทางกำหนดแบบเฉพาะ",
        "ลงวันที่ในเอกสารหลังวันยื่นคำขอ",
        "ชื่อผู้ขอในเอกสารต่างจากทะเบียนนิติบุคคล",
        "แปลรายการข้อถือสิทธิโดยไม่คงโครงสร้างประโยคเดิม",
        "ข้ามลำดับการรับรอง ทำให้ต้องเซ็นเอกสารใหม่ทั้งชุด",
      ],
      compare: [
        { option: "แปลรับรองอย่างเดียว", useWhen: "ยื่นในประเทศหรือปลายทางรับคำรับรองบริษัทแปล", time: "2–5 วันทำการ", note: "เร็วที่สุด" },
        { option: "แปล + รับรองทนาย", useWhen: "นายทะเบียนขอลายมือชื่อรับรอง", time: "3–6 วันทำการ", note: "ใช้บ่อยกับงานข้ามประเทศ" },
        { option: "ครบสายถึงสถานทูต", useWhen: "นายทะเบียนต่างประเทศบังคับ", time: "7–15 วันทำการ", note: "ขึ้นกับคิวสถานทูต" },
      ],
      faqs: [
        { q: "POA ต้องรับรองทุกครั้งไหม", a: "ไม่เสมอไป หลายประเทศรับ POA ธรรมดา แต่บางประเทศบังคับให้รับรองครบสาย เราตรวจให้ก่อนเริ่ม" },
        { q: "แปลข้อถือสิทธิสิทธิบัตรได้ไหม", a: "ได้ โดยใช้นักแปลสายเทคนิคและคงโครงสร้างข้อถือสิทธิตามต้นฉบับ" },
        { q: "ต้องใช้หนังสือรับรองบริษัทใหม่แค่ไหน", a: "ปลายทางส่วนใหญ่ขอฉบับคัดไม่เกิน 3–6 เดือน" },
        { q: "ผู้ลงนามต้องเป็นกรรมการเท่านั้นไหม", a: "ต้องเป็นผู้มีอำนาจตามหนังสือรับรอง หรือผู้รับมอบอำนาจที่มีเอกสารรองรับ" },
        { q: "รับงานเร่งด่วนก่อนวันครบกำหนดยื่นได้ไหม", a: "รับ แต่ควรแจ้งวันครบกำหนดล่วงหน้า เพื่อวางลำดับรับรองให้ทัน" },
        { q: "ใช้กับการยื่นผ่านระบบมาดริดได้ไหม", a: "ได้ เอกสารประกอบที่ต้องแปลและรับรองใช้แนวทางเดียวกัน" },
        { q: "เก็บความลับทางเทคนิคอย่างไร", a: "ทำ NDA จำกัดผู้เข้าถึง และใช้ช่องทางส่งไฟล์ที่ควบคุมได้" },
        { q: "แปลกลับเป็นไทยเพื่อยื่นในประเทศได้ไหม", a: "ได้ และเราจัดรูปแบบให้ตรงกับที่กรมทรัพย์สินทางปัญญาใช้จริง" },
      ],
      consultant:
        "งาน IP แพ้ทางวันที่และถ้อยคำ ทีมงานกว่า 15 ปีจะตรวจแบบฟอร์ม ลำดับการรับรอง และวันครบกำหนดยื่นให้ก่อนเริ่มงาน หากไม่อยากประสานหลายฝ่าย ให้เราดูแลตั้งแต่แปลจนถึงรับรองสถานทูต",
      related: [
        ["แปลเอกสารรับรอง", "/translation"],
        ["บริการรับรองเอกสารโดยทนาย Notary", "/notary"],
        ["รับรองเอกสารกงสุลและสถานทูต", "/mfa-legalisation"],
      ],
    },
    en: {
      title: "Trademark and IP document certification",
      h1: "Trademark, patent and copyright documents: certified translation and legalisation",
      metaTitle: "Trademark and IP Document Translation and Certification",
      metaDesc:
        "Certified translation and notarial certification of IP powers of attorney, deeds of assignment, patent and trademark filings, with consular legalisation for foreign registries.",
      answer:
        "Cross-border trademark and patent filings usually need a power of attorney, a deed of assignment and the applicant's corporate documents, each with a certified translation. Translation and notarial certification typically take two to five working days; add five to ten working days if the destination registry requires consular legalisation.",
      audience: "Companies, IP agents and law firms filing applications in Thailand or abroad.",
      facts: [
        { label: "Common documents", value: "POA, deed of assignment, company affidavit and chain-of-title records" },
        { label: "Translation and certification", value: "2–5 working days" },
        { label: "Consular and embassy", value: "Add 5–10 working days" },
        { label: "Dates matter", value: "Document dates must align with filing and priority dates" },
        { label: "Name consistency", value: "Applicant names must match the corporate register everywhere" },
      ],
      documents: [
        "Power of attorney in the destination registry's prescribed form",
        "Deed of assignment between inventor and applicant where relevant",
        "Recently issued company affidavit",
        "Filing details such as application number, classes or claim set",
        "Passport or ID of the signatory",
      ],
      steps: [
        { title: "Check the registry form", detail: "Confirm whether a prescribed POA form and a specific certification level apply.", who: "Document consultant", eta: "Same day" },
        { title: "Fix technical terminology", detail: "Align technical terms and product names across the whole set.", who: "Technical translator", eta: "1 working day" },
        { title: "Translate and review", detail: "Re-check application numbers, dates and claim wording.", who: "Translation and review team", eta: "2–5 working days" },
        { title: "Sign and certify", detail: "The authorised signatory signs before the attorney, who issues the certificate.", who: "Notarial attorney", eta: "1 working day" },
        { title: "Consular and embassy legalisation", detail: "Complete the chain where the destination registry requires it.", who: "Filing team", eta: "5–10 working days" },
      ],
      pitfalls: [
        "Using a generic POA where a prescribed form is mandatory",
        "Dating documents after the filing date",
        "Applicant name differing from the corporate register",
        "Translating claims without preserving the original structure",
        "Skipping a legalisation step, forcing the set to be re-signed",
      ],
      compare: [
        { option: "Certified translation only", useWhen: "Domestic filing or a registry that accepts company certificates", time: "2–5 working days", note: "Fastest option" },
        { option: "Translation plus notarial certification", useWhen: "The registry requires a certified signature", time: "3–6 working days", note: "Common for cross-border filings" },
        { option: "Full chain to the embassy", useWhen: "Mandated by a foreign registry", time: "7–15 working days", note: "Depends on embassy queues" },
      ],
      faqs: [
        { q: "Must every POA be legalised?", a: "No. Many registries accept a simple POA, while others require the full chain. We check before starting." },
        { q: "Can you translate patent claims?", a: "Yes, with technical translators who preserve the claim structure." },
        { q: "How recent must the company affidavit be?", a: "Most destinations require issuance within three to six months." },
        { q: "Must a director sign?", a: "The signatory must have authority under the affidavit, or hold a documented delegation." },
        { q: "Can you handle deadline-driven rush work?", a: "Yes, but tell us the deadline early so the legalisation order can be planned." },
        { q: "Does this work for Madrid System filings?", a: "Yes, supporting documents follow the same translation and certification approach." },
        { q: "How is technical confidentiality handled?", a: "NDA, restricted access and controlled file transfer channels." },
        { q: "Can you translate into Thai for local filing?", a: "Yes, formatted the way the Department of Intellectual Property expects." },
      ],
      consultant:
        "IP work fails on dates and wording. Our 15+ year team verifies forms, certification order and filing deadlines before work starts, and can run everything from translation to embassy legalisation.",
      related: [
        ["Certified translation", "/en/translation"],
        ["Notary Public service", "/en/notary"],
        ["MFA and embassy legalisation", "/en/mfa-legalisation"],
      ],
    },
  },
  {
    slug: "export-trade-document-certification",
    cluster: "certified",
    th: {
      title: "รับรองเอกสารการค้าและการส่งออกสำหรับปลายทางต่างประเทศ",
      h1: "รับรองเอกสารส่งออก ใบกำกับการค้า หนังสือรับรองแหล่งกำเนิดสินค้า และเอกสารประกอบ",
      metaTitle: "รับรองเอกสารส่งออก Invoice CO และเอกสารการค้าครบสาย",
      metaDesc:
        "แปลและรับรองเอกสารการค้าและการส่งออก ใบกำกับการค้า Packing List หนังสือรับรองแหล่งกำเนิดสินค้า และหนังสือรับรองบริษัท พร้อมรับรองกงสุลและสถานทูตตามที่ปลายทางกำหนด",
      answer:
        "ผู้ซื้อและหน่วยงานศุลกากรบางประเทศ โดยเฉพาะกลุ่มตะวันออกกลาง กำหนดให้เอกสารการค้า เช่น ใบกำกับการค้า Packing List และหนังสือรับรองแหล่งกำเนิดสินค้า ต้องผ่านการรับรองก่อนใช้ ขั้นตอนโดยทั่วไปคือรับรองจากหน่วยงานที่เกี่ยวข้อง ตามด้วยกรมการกงสุล และสถานทูตปลายทาง ใช้เวลารวมประมาณ 5–12 วันทำการขึ้นกับคิวของแต่ละสถานทูต",
      audience: "ผู้ส่งออก ผู้นำเข้า ฟอร์เวิร์ดเดอร์ และฝ่ายเอกสารการค้าที่ต้องส่งชุดเอกสารตามเงื่อนไข L/C หรือข้อกำหนดปลายทาง",
      facts: [
        { label: "เอกสารที่ต้องรับรองบ่อย", value: "Commercial Invoice, Packing List, Certificate of Origin, หนังสือรับรองบริษัท" },
        { label: "ระยะเวลารวมโดยทั่วไป", value: "5–12 วันทำการ" },
        { label: "ลำดับที่ถูกต้อง", value: "หน่วยงานต้นทาง → กรมการกงสุล → สถานทูตปลายทาง" },
        { label: "จุดตรวจสำคัญ", value: "ชื่อผู้ซื้อ ผู้ขาย เลขอินวอยซ์ และรายละเอียดสินค้าต้องตรงกันทุกฉบับ" },
        { label: "เงื่อนไข L/C", value: "ต้องอ่านเงื่อนไขก่อน เพราะบางฉบับระบุถ้อยคำรับรองเฉพาะ" },
      ],
      documents: [
        "ใบกำกับการค้า (Commercial Invoice) ฉบับลงนาม",
        "Packing List ที่สอดคล้องกับอินวอยซ์",
        "หนังสือรับรองแหล่งกำเนิดสินค้า",
        "หนังสือรับรองบริษัทฉบับคัดใหม่",
        "เงื่อนไข L/C หรือหนังสือสั่งซื้อจากผู้ซื้อปลายทาง",
      ],
      steps: [
        { title: "อ่านเงื่อนไขปลายทางและ L/C", detail: "สรุปว่าต้องรับรองกี่ชั้น และมีถ้อยคำบังคับใดในเอกสาร", who: "ที่ปรึกษาเอกสารการค้า", eta: "ภายในวันเดียว" },
        { title: "ตรวจความสอดคล้องของชุดเอกสาร", detail: "ตรวจชื่อ ที่อยู่ เลขอินวอยซ์ น้ำหนัก และจำนวนให้ตรงกันทุกฉบับ", who: "ทีมตรวจเอกสาร", eta: "1 วันทำการ" },
        { title: "รับรองต้นทาง", detail: "ดำเนินการรับรองจากหน่วยงานต้นทางที่เกี่ยวข้องกับเอกสารแต่ละประเภท", who: "ทีมเดินเอกสาร", eta: "1–3 วันทำการ" },
        { title: "รับรองกรมการกงสุล", detail: "ยื่นรับรองที่กรมการกงสุล กระทรวงการต่างประเทศ", who: "ทีมเดินเอกสาร", eta: "2–4 วันทำการ" },
        { title: "รับรองสถานทูตปลายทาง", detail: "ยื่นสถานทูตปลายทางเป็นขั้นสุดท้ายและส่งมอบชุดพร้อมใช้", who: "ทีมเดินเอกสาร", eta: "2–5 วันทำการ" },
      ],
      pitfalls: [
        "รายละเอียดสินค้าในอินวอยซ์กับ Packing List ไม่ตรงกัน",
        "ลงนามไม่ครบหรือใช้ลายเซ็นสแกนทั้งที่ปลายทางต้องการลายเซ็นสด",
        "ข้ามขั้นกรมการกงสุลแล้วไปสถานทูตโดยตรง",
        "ไม่ตรวจถ้อยคำบังคับตาม L/C ทำให้ธนาคารปฏิเสธชุดเอกสาร",
        "เผื่อเวลาไม่พอก่อนเรือออกหรือก่อนวันหมดอายุ L/C",
      ],
      compare: [
        { option: "รับรองต้นทางอย่างเดียว", useWhen: "ผู้ซื้อรับเอกสารรับรองระดับหน่วยงานต้นทาง", time: "1–3 วันทำการ", note: "เร็วที่สุด" },
        { option: "รับรองถึงกรมการกงสุล", useWhen: "ปลายทางขอการรับรองจากรัฐไทย", time: "3–7 วันทำการ", note: "ชุดที่ใช้บ่อย" },
        { option: "ครบสายถึงสถานทูต", useWhen: "ศุลกากรปลายทางบังคับ เช่นหลายประเทศตะวันออกกลาง", time: "5–12 วันทำการ", note: "ต้องเผื่อคิวสถานทูต" },
      ],
      faqs: [
        { q: "ต้องใช้ต้นฉบับหรือสำเนา", a: "ส่วนใหญ่ต้องใช้ต้นฉบับลงนามสด สำเนาสแกนมักไม่ได้รับการรับรอง" },
        { q: "รับรองกี่ชุดดี", a: "ทำตามจำนวนที่ผู้ซื้อและธนาคารกำหนด และเผื่อสำรองอย่างน้อยหนึ่งชุด" },
        { q: "แก้ไขอินวอยซ์หลังรับรองได้ไหม", a: "ไม่ได้ ต้องออกฉบับใหม่และรับรองใหม่ทั้งสาย" },
        { q: "ใช้เวลานานสุดตรงไหน", a: "ขั้นสถานทูตปลายทาง เพราะขึ้นกับคิวและวันทำการของแต่ละแห่ง" },
        { q: "ช่วยตรวจเงื่อนไข L/C ให้ได้ไหม", a: "ได้ เราอ่านเงื่อนไขและสรุปรายการเอกสารพร้อมถ้อยคำบังคับก่อนเริ่ม" },
        { q: "แปลเอกสารเป็นภาษาปลายทางด้วยไหม", a: "ทำได้ เช่น ภาษาอาหรับหรือจีน พร้อมคำรับรองการแปล" },
        { q: "งานด่วนก่อนเรือออกทำได้ไหม", a: "ทำได้ในบางกรณีโดยจัดคิวยื่นแบบเร่ง แต่ยังขึ้นกับตารางของหน่วยงาน" },
        { q: "หลังไทยเข้าเป็นภาคี Apostille แล้วต้องรับรองสถานทูตอีกไหม", a: "การเข้าเป็นภาคีมีผลใช้บังคับ 28 กุมภาพันธ์ 2570 และการยอมรับขึ้นกับปลายทาง ก่อนหน้านั้นยังใช้สายกงสุลตามเดิม" },
      ],
      consultant:
        "งานเอกสารส่งออกพลาดหนึ่งจุดคือชุดเอกสารถูกปฏิเสธทั้งชุดและกระทบตารางเรือ ทีมงานกว่า 15 ปีจะตรวจความสอดคล้องทุกฉบับและวางลำดับรับรองให้ทันกำหนด หากไม่อยากวิ่งหลายหน่วยงาน ให้เราเดินเอกสารให้ครบสาย",
      related: [
        ["รับรองเอกสารกงสุลและสถานทูต", "/mfa-legalisation"],
        ["บริการรับรองเอกสารโดยทนาย Notary", "/notary"],
        ["แปลเอกสารรับรอง", "/translation"],
      ],
    },
    en: {
      title: "Export and trade document certification",
      h1: "Export document legalisation: invoices, packing lists and certificates of origin",
      metaTitle: "Export Trade Document Legalisation Invoice CO Thailand",
      metaDesc:
        "Translate and legalise commercial invoices, packing lists, certificates of origin and company affidavits through the correct chain to the destination embassy for customs clearance.",
      answer:
        "Some buyers and customs authorities, notably across the Middle East, require trade documents such as commercial invoices, packing lists and certificates of origin to be legalised before use. The usual chain runs from the issuing body to the Department of Consular Affairs and then the destination embassy, taking about five to twelve working days depending on embassy queues.",
      audience: "Exporters, importers, freight forwarders and trade documentation teams working to L/C terms or destination requirements.",
      facts: [
        { label: "Documents commonly legalised", value: "Commercial invoice, packing list, certificate of origin, company affidavit" },
        { label: "Typical total duration", value: "5–12 working days" },
        { label: "Correct order", value: "Issuing body → Department of Consular Affairs → destination embassy" },
        { label: "Key checks", value: "Buyer, seller, invoice number and goods details must match across every document" },
        { label: "L/C terms", value: "Read them first; some prescribe exact certification wording" },
      ],
      documents: [
        "Signed commercial invoice",
        "Packing list consistent with the invoice",
        "Certificate of origin",
        "Recently issued company affidavit",
        "L/C terms or the buyer's purchase order",
      ],
      steps: [
        { title: "Read destination and L/C terms", detail: "Establish how many certification layers and which mandatory wording apply.", who: "Trade document consultant", eta: "Same day" },
        { title: "Check set consistency", detail: "Verify names, addresses, invoice numbers, weights and quantities across documents.", who: "Document review team", eta: "1 working day" },
        { title: "Origin-side certification", detail: "Obtain certification from the relevant issuing body for each document type.", who: "Filing team", eta: "1–3 working days" },
        { title: "Consular Affairs legalisation", detail: "File with the Department of Consular Affairs.", who: "Filing team", eta: "2–4 working days" },
        { title: "Destination embassy", detail: "Complete the final step and deliver a ready-to-use set.", who: "Filing team", eta: "2–5 working days" },
      ],
      pitfalls: [
        "Goods details differing between invoice and packing list",
        "Missing signatures or scanned signatures where wet ink is required",
        "Going to the embassy before the Department of Consular Affairs",
        "Ignoring mandatory L/C wording, leading to bank rejection",
        "Leaving too little time before sailing or L/C expiry",
      ],
      compare: [
        { option: "Origin-side certification only", useWhen: "The buyer accepts issuing-body certification", time: "1–3 working days", note: "Fastest option" },
        { option: "Chain to Consular Affairs", useWhen: "The destination requires Thai state authentication", time: "3–7 working days", note: "The common set" },
        { option: "Full chain to the embassy", useWhen: "Required by destination customs, common in the Middle East", time: "5–12 working days", note: "Allow for embassy queues" },
      ],
      faqs: [
        { q: "Originals or copies?", a: "Wet-signed originals are usually required; scanned copies are generally not accepted for legalisation." },
        { q: "How many sets should be legalised?", a: "Match the buyer and bank requirement and keep at least one spare set." },
        { q: "Can an invoice be amended after legalisation?", a: "No. A new document must be issued and legalised again." },
        { q: "Which step takes longest?", a: "The destination embassy, because of queues and limited service days." },
        { q: "Can you review L/C terms?", a: "Yes, we summarise the required documents and mandatory wording before starting." },
        { q: "Do you translate into the destination language?", a: "Yes, including Arabic and Chinese, with a translation certificate." },
        { q: "Can you handle pre-sailing rush jobs?", a: "Often yes, with expedited filing slots, subject to each authority's schedule." },
        { q: "Will Apostille remove the embassy step?", a: "Thailand's accession takes effect on 28 February 2027 and acceptance depends on the destination; until then the consular chain applies." },
      ],
      consultant:
        "One mismatch can get an entire trade set rejected and disrupt the shipping schedule. Our 15+ year team checks consistency across documents and sequences legalisation to your deadline, or runs the whole chain for you.",
      related: [
        ["MFA and embassy legalisation", "/en/mfa-legalisation"],
        ["Notary Public service", "/en/notary"],
        ["Certified translation", "/en/translation"],
      ],
    },
  },
];
